import { NgIf } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { ChangeDetectorRef } from '@angular/core';
import { AbstractControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormHelper } from '@common/form-helper';
import { FormService } from '@services/form.service';

@Component({
  selector: 'app-height-weight-input',
  templateUrl: './height-weight-input.component.html',
  styleUrls: ['./height-weight-input.component.scss'],
  imports: [NgIf, FormsModule, ReactiveFormsModule],
})
export class HeightWeightInputComponent extends FormHelper implements OnInit {
  @Input() public form: FormGroup;
  @Input() public groupName: string = '';
  @Input() public requireHeightAndWeight: boolean = false;

  public formGroup: FormGroup;

  private _dob: Date | null;

  private formService = inject(FormService);
  private cdRef = inject(ChangeDetectorRef);

  /**
   * Determines if the patient requires height and weight fields to be filled out.
   */
  public get patientRequiresHeightAndWeight() {
    return this.requireHeightAndWeight || (this._dob ? this.calculatePatientAge(this._dob) <= 19 : true);
  }

  @Input()
  public set dob(value: Date | null) {
    if (value?.getTime() === this._dob?.getTime()) {
      return;
    }

    this._dob = value;
    this.adjustHeightAndWeightValidators();
  }

  /**
   * Initialize the component and subscribe to birthday changes.
   */
  public ngOnInit(): void {
    this.formGroup = (this.form?.get(this.groupName) as FormGroup) ?? this.form;
    if (this.formGroup) {
      this.adjustHeightAndWeightValidators();
    }
  }

  /**
   * Prevents invalid input for height and weight fields.
   *
   * @param {KeyboardEvent} event The keyboard event triggered by the user.
   */
  public preventInvalidInput(event: KeyboardEvent): void {
    const invalidKeys = [',', '.'];

    if (invalidKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  /**
   * Determines if a form control is invalid.
   */
  public isControlInvalid(controlName: string): boolean {
    return this.isInvalid(this.formGroup?.get(controlName));
  }

  /**
   * Generates the error messages for a given form control.
   *
   * @param controlName The form control name
   * @param friendlyName The user-facing name of the form control
   */
  public generateErrorMessages(controlName: string, friendlyName: string): string {
    const { required, max, min } = this.formGroup?.get(controlName).errors || {};

    if (required) {
      return `Your ${friendlyName} is required.`;
    }

    if (max) {
      return `The maximum allowed value for ${friendlyName} is ${max.max}.`;
    }

    if (min) {
      return `The minimum allowed value for ${friendlyName} is ${min.min}.`;
    }

    return '';
  }

  /**
   * Determines the qa attribute for a given qa descriptor.
   */
  public getDynamicDataQaAttribute(qaDescriptor: string): string {
    return this.groupName ? `${this.groupName}-${qaDescriptor}` : `${qaDescriptor}`;
  }

  /**
   * Adjust the validators for the height and weight fields based on the patient's age.
   */
  private adjustHeightAndWeightValidators(): void {
    if (!this.formGroup) {
      return;
    }

    const controlsToCheck = ['height_feet', 'height_inches', 'weight'];

    const action = this.patientRequiresHeightAndWeight
      ? (control: string) => {
          this.formGroup.get(control).addValidators(Validators.required);
          this.formGroup.get(control).updateValueAndValidity();
        }
      : (control: string) => this.resetControlAndValue(this.formGroup.get(control));

    controlsToCheck.forEach(action);
    this.cdRef.detectChanges();
  }

  /**
   * Reset the control validators and value.
   *
   * @param control The form control
   */
  private resetControlAndValue(control: AbstractControl): void {
    control.reset();
    this.formService.resetControl(control);
  }
}
